import { useCallback, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"
import { delay } from "motion/react"
import {
	IconArrowDownToLineOutline18 as Download,
	IconShareUpRightOutline18 as ExternalLink,
	IconEyeSlashOutline18 as EyeOff,
	IconNotepadOutline18 as FileText,
	IconCodePullRequestOutline18 as GitPullRequest,
	IconClockTimeOutline18 as History,
	IconLinkOutline18 as LinkIcon,
	IconMoveToRightOutline18 as MoveToPortal,
	IconAsteriskOutline18 as SquareAsterisk,
	IconArrowsLeftRightTrailOutline18 as SuggestionsIcon,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import type { HelpCenterArticle } from "@productlane/zero"
import {
	cn,
	getDocsArticleUrl,
	getPortalUrl,
} from "@productlane/lib/src/browser"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import {
	AlertDialog,
	Button,
	IconButton,
	Shortcut,
	Tooltip,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { Language } from "../../settings/settings-pages/language-selector"
import type { TranslationTarget } from "@/lib/translation-status"
import { DocViewers } from "@/components/doc-viewers"
import { SubscribeButton } from "@/components/notifications/subscribe-button"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { TranslateWithAIButton } from "@/components/translate-with-ai-button"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { isQuerySettled } from "@/lib/query-settled"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { useActivePortalInstanceStore } from "@/stores/active-portal-instance-store"
import { LanguageSelector } from "../../settings/settings-pages/language-selector"
import { MobileBackButton } from "../mobile-back-button"
import { PublishPortalPageButton } from "../portal/publish-portal-page-button"
import { shouldRejectKeystrokes } from "../sidebar"
import { CopilotToggleButton } from "../threads/components/right-bar/ai-chat/copilot-toggle-button"
import { getGoLiveButtonLabel } from "./article-visibility"
import { ArticleVisibilityButton } from "./article-visibility-button"
import { DocsHeaderTabs } from "./docs-header-tabs"
import { HelpCenterSettings } from "./help-center-settings"
import { MoveToPortalDialog } from "./move-to-portal-dialog"
import { restoreArticleVersion } from "./restore-article-version"
import { SuggestionHistoryDialog } from "./suggestion-history-dialog"
import { VersionHistoryDialog } from "./version-history-dialog"

interface HelpCenterHeaderProps {
	article?: HelpCenterArticle
	className?: string
	selectedLanguage: Language
	setSelectedLanguage: (language: Language) => void
	translationTargets?: readonly TranslationTarget[]
	// Beside an expanded assistant chat, which is already the copilot.
	hideCopilot?: boolean
}

export function HelpCenterHeader({
	article,
	className,
	selectedLanguage,
	setSelectedLanguage,
	translationTargets = [],
	hideCopilot = false,
}: HelpCenterHeaderProps) {
	const [isDeleteOpen, setIsDeleteOpen] = useState(false)
	const [isExporting, setIsExporting] = useState(false)

	const navigate = useNavigate()
	const zero = useZero()
	const workspace = useZeroWorkspace()
	const { role } = useRole()
	const enabledLanguages = workspace?.enabledLanguages ?? ["en"]

	const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false)
	const [isSuggestionHistoryOpen, setIsSuggestionHistoryOpen] = useState(false)
	const [isMoveToPortalOpen, setIsMoveToPortalOpen] = useState(false)
	// "Propose edit" only makes sense on the main (root) portal — proposals
	// live there for now; other portals are coming later.
	const activePortalInstanceId = useActivePortalInstanceStore(
		(s) => s.activePortalInstanceId,
	)
	const isMainPortal = activePortalInstanceId === null
	const hasOtherPortals = (workspace?.portalInstances ?? []).length >= 1
	// Named portals serve their pages under a `/{slug}` prefix, so a public link
	// without it lands on the root portal and 404s.
	const portalSlugFor = (portalInstanceId: string | null | undefined) =>
		(workspace?.portalInstances ?? []).find(
			(instance) => instance.id === portalInstanceId,
		)?.slug ?? null
	const articlePortalSlug = portalSlugFor(article?.portalInstanceId)
	const activePortalSlug = portalSlugFor(activePortalInstanceId)

	const [versions, versionsResult] = useQuery(
		queries.helpCenterArticleVersions({
			articleId: article?.id ?? "",
			language: selectedLanguage.code,
		}),
	)

	const [closedProposals, closedProposalsResult] = useQuery(
		queries.closedProposalsForArticle(article?.id ?? ""),
	)

	const defaultLanguage = workspace?.defaultLanguage ?? "en"

	useShortcut(
		"meta+backspace",
		(e) => {
			e.preventDefault()
			e.stopPropagation()
			setIsDeleteOpen(true)
		},
		{
			enabled: role !== "VIEWER" && !!article,
			enableOnContentEditable: true,
			enableOnFormTags: true,
			skipDefaultGuards: true,
			ignoreEventWhen: () => shouldRejectKeystrokes() ?? false,
		},
	)

	const copyArticleId = async (articleId: string) => {
		await navigator.clipboard.writeText(articleId)
		toast.info({
			title: "Copied article ID to clipboard",
		})
		analytics.track("copy-helpcenterarticle-id", {
			id: articleId,
		})
	}

	const copyArticleUrl = async (articleId: string) => {
		if (!workspace) return
		await navigator.clipboard.writeText(
			getDocsArticleUrl(workspace, articleId, articlePortalSlug),
		)
		toast.info({
			title: "Copied article URL to clipboard",
		})
		analytics.track("copy-helpcenterarticle-url", {
			id: articleId,
		})
	}

	useShortcut(
		"mod+period",
		() => {
			if (article) void copyArticleId(article.id)
		},
		{ preventDefault: true, enabled: !!article },
	)

	useShortcut(
		"meta+shift+,",
		() => {
			if (article) {
				void copyArticleUrl(article.id)
			}
		},
		{
			enableOnContentEditable: false,
			// default splitKey is "," which would break a comma hotkey
			splitKey: "~",
		},
	)

	const exportSingleDocMutation = api.workspace.exportSingleDoc.useMutation()

	const handleExport = useCallback(
		async (format: "markdown" | "pdf") => {
			if (!article) return

			setIsExporting(true)
			const exportPromise = exportSingleDocMutation
				.mutateAsync({ articleId: article.id, format })
				.then((data) => {
					let blob: Blob

					if (data.mimeType === "application/pdf") {
						// PDF comes as base64 from backend, decode it
						const binaryString = window.atob(data.data)
						const bytes = new Uint8Array(binaryString.length)
						for (let i = 0; i < binaryString.length; i++) {
							bytes[i] = binaryString.charCodeAt(i)
						}
						blob = new Blob([bytes], { type: "application/pdf" })
					} else {
						blob = new Blob([data.data], { type: data.mimeType })
					}

					const link = document.createElement("a")
					const url = URL.createObjectURL(blob)
					link.setAttribute("href", url)
					link.setAttribute("download", data.filename)
					link.style.visibility = "hidden"
					document.body.appendChild(link)
					link.click()
					document.body.removeChild(link)
					URL.revokeObjectURL(url)

					analytics.track("helpcenterarticle-export", {
						id: article.id,
						format,
					})
				})

			void toast.promise(exportPromise, {
				loading: `Preparing ${format === "pdf" ? "PDF" : "Markdown"} document, this can take a moment.`,
				success: "Article exported successfully",
				error: "Failed to export article. Please try again.",
			})

			try {
				await exportPromise
			} catch {
				// surfaced by toast.promise
			} finally {
				setIsExporting(false)
			}
		},
		[article, exportSingleDocMutation],
	)

	if (!workspace) {
		return null
	}

	return (
		<>
			{article && (
				<>
					<AlertDialog
						isOpen={isDeleteOpen}
						onOpenChange={setIsDeleteOpen}
						confirmText="Delete"
						title={
							article.title
								? `Delete "${article.title}"?`
								: "Delete this article?"
						}
						description={
							article.title
								? `Are you sure you want to delete the article "${article.title}"?`
								: "Are you sure you want to delete this article?"
						}
						onCancel={() => {
							analytics.track("cancel-delete-helpcenterarticle", {
								id: article.id,
							})
							setIsDeleteOpen(false)
						}}
						onConfirm={() => {
							setIsDeleteOpen(false)
							// eslint-disable-next-line @typescript-eslint/no-misused-promises
							delay(async () => {
								await mutateOk(
									zero.mutate(
										mutators.helpCenterArticles.delete({
											id: article.id,
										}),
									),
								)
								// same length as the alert animation
							}, 200)

							void navigate({ to: "/edit-docs" })
							analytics.track("helpcenterarticle-delete", { id: article.id })
						}}
					/>

					{isMoveToPortalOpen && (
						<MoveToPortalDialog
							isOpen={isMoveToPortalOpen}
							onClose={() => setIsMoveToPortalOpen(false)}
							target={{
								kind: "article",
								id: article.id,
								name: article.title || "Untitled",
								groupId: article.helpCenterGroupId,
								portalInstanceId: article.portalInstanceId ?? null,
							}}
						/>
					)}

					<SuggestionHistoryDialog
						proposals={closedProposals}
						isLoading={!isQuerySettled(closedProposalsResult)}
						articleTitle={article.title}
						articleIcon={article.icon}
						isOpen={isSuggestionHistoryOpen}
						onOpenChange={setIsSuggestionHistoryOpen}
					/>

					<VersionHistoryDialog
						versions={versions}
						isLoading={!isQuerySettled(versionsResult)}
						articleTitle={article.title}
						articleIcon={article.icon}
						isOpen={isVersionHistoryOpen}
						onOpenChange={setIsVersionHistoryOpen}
						onRestore={async (version) => {
							if (!workspace) return
							const restored = await restoreArticleVersion({
								zero,
								workspaceId: workspace.id,
								articleId: article.id,
								version,
								defaultLanguage,
							})
							if (restored) setIsVersionHistoryOpen(false)
						}}
					/>
				</>
			)}
			<div
				className={cn(
					"flex max-h-12 min-h-12 w-full flex-wrap items-center justify-end gap-2 border-b border-gray-200 py-2 pl-[17.5px] pr-3 backdrop-blur-xl dark:border-gray-700/80 dark:bg-gray-900/80",
					className,
				)}
			>
				<DocsHeaderTabs />
				{article && (
					<div className="relative z-20 mr-auto sm:hidden">
						<MobileBackButton to="/edit-docs" label="Back to docs" />
					</div>
				)}
				<div className="flex flex-row items-center gap-2">
					{article && (
						<DocViewers
							docKey={`article:${article.id}:${selectedLanguage.code}`}
						/>
					)}
					<RoleCheck not={["VIEWER"]}>
						{article && (
							<TranslateWithAIButton
								resource="helpCenterArticle"
								resourceId={article.id}
								targets={translationTargets}
							/>
						)}
						{enabledLanguages.length > 1 && (
							<LanguageSelector
								languages={enabledLanguages}
								selectedLanguage={selectedLanguage}
								onLanguageSelect={setSelectedLanguage}
								variant="code-only"
							/>
						)}
					</RoleCheck>
					<RoleCheck not={["VIEWER"]}>
						{article && (
							<ArticleVisibilityButton
								articleId={article.id}
								visibility={article.visibility}
								article={{
									urlName: article.urlName,
									previousUrlNames: article.previousUrlNames,
									published: article.published,
									title: article.title,
									helpCenterGroupId: article.helpCenterGroupId,
								}}
							/>
						)}
						{article && !article.published && (
							<Button
								onClick={async () => {
									await mutateOk(
										zero.mutate(
											mutators.helpCenterArticles.update({
												id: article.id,
												published: true,
											}),
										),
									)
									toast.success(
										article.visibility !== "PUBLIC"
											? {
													title: "Article saved",
													description: `${article.title || "New article"} is live for your team.`,
												}
											: {
													title: "Article published",
													description: `${article.title || "New article"} is now public.`,
												},
									)
									analytics.track("helpcenterarticle-publish", {
										id: article.id,
									})
								}}
							>
								{getGoLiveButtonLabel(article.visibility)}
							</Button>
						)}
					</RoleCheck>

					{/* The settings button lives on the docs home; an open article just
						    gets a link out to its published page. */}
					{/* One container owns the icon spacing: an element that renders
						    nothing then cannot consume a row gap and stretch the cluster. */}
					<div className="ml-1 flex items-center gap-1">
						{article
							? // Hidden articles have no public page to open.
								article.published &&
								article.visibility === "PUBLIC" && (
									<Tooltip label="Open article">
										<IconButton
											Icon={ExternalLink}
											variant="ghost"
											onClick={() =>
												window.open(
													getPortalUrl(
														workspace,
														`/docs${article.urlName ? `/${article.urlName}` : ""}`,
														articlePortalSlug,
													),
													"_blank",
												)
											}
										/>
									</Tooltip>
								)
							: workspace && (
									<PublishPortalPageButton
										content={<HelpCenterSettings />}
										name="docs"
										url={getPortalUrl(workspace, "/docs", activePortalSlug)}
										isPublished={workspace.helpCenterPublic}
									/>
								)}
						{/* No wrapper element: the toggle renders nothing while the panel
						    is open, and an empty wrapper would still consume a gap. */}
						{article && !hideCopilot && (
							<CopilotToggleButton
								scope={{ kind: "article", articleId: article.id }}
							/>
						)}
						{article && (
							<SubscribeButton entityType="docsArticle" entityId={article.id} />
						)}
						{article && (
							<span className="flex items-center">
								<RoleCheck not={["VIEWER"]}>
									<DropdownMenu
										trigger={
											<IconButton
												Icon={MoreVertical}
												variant="ghost"
												iconClassName="fill-current"
											/>
										}
										contentProps={{
											side: "bottom",
											align: "end",
										}}
									>
										<DropdownMenuGroup>
											{article.published && (
												<DropdownMenuItem
													Icon={EyeOff}
													onClick={async () => {
														await mutateOk(
															zero.mutate(
																mutators.helpCenterArticles.update({
																	id: article.id,
																	published: false,
																}),
															),
														)
														analytics.track("helpcenterarticle-unpublish", {
															id: article.id,
														})
													}}
												>
													Unpublish
												</DropdownMenuItem>
											)}
											{isMainPortal && (
												<DropdownMenuItem
													Icon={GitPullRequest}
													onClick={() => {
														analytics.track("open-helpcenterproposal-compose", {
															id: article.id,
														})
														void navigate({
															to: "/edit-docs/$id/propose",
															params: { id: article.id },
														})
													}}
												>
													Propose edit
												</DropdownMenuItem>
											)}
											{hasOtherPortals && (
												<DropdownMenuItem
													Icon={MoveToPortal}
													onClick={() => {
														analytics.track("open-move-to-portal", {
															id: article.id,
														})
														setIsMoveToPortalOpen(true)
													}}
												>
													Move to portal
												</DropdownMenuItem>
											)}
											<DropdownMenuItem
												Icon={History}
												onClick={() => {
													setIsVersionHistoryOpen(true)
													analytics.track("open-article-version-history", {
														id: article.id,
													})
												}}
											>
												Version History
											</DropdownMenuItem>
											<DropdownMenuItem
												Icon={SuggestionsIcon}
												onClick={() => {
													setIsSuggestionHistoryOpen(true)
													analytics.track("open-article-suggestion-history", {
														id: article.id,
													})
												}}
											>
												Reviewed Suggestions
											</DropdownMenuItem>
											<DropdownMenuItem
												Icon={SquareAsterisk}
												onClick={async () => {
													await copyArticleId(article.id)
												}}
												className="flex w-full"
											>
												<div className="flex w-full items-center justify-between">
													<div>Copy Article ID</div>
													<div className="flex gap-1">
														<Shortcut>{ShortcutSymbols.CMD}</Shortcut>
														<Shortcut>{ShortcutSymbols["."]}</Shortcut>
													</div>
												</div>
											</DropdownMenuItem>
											<DropdownMenuItem
												Icon={LinkIcon}
												onClick={async () => {
													await copyArticleUrl(article.id)
												}}
												className="flex w-full"
											>
												<div className="flex w-full items-center justify-between">
													<div>Copy Article URL</div>
													<div className="flex gap-1">
														<Shortcut>{ShortcutSymbols.CMD}</Shortcut>
														<Shortcut>{ShortcutSymbols.SHIFT}</Shortcut>
														<Shortcut>{ShortcutSymbols[","]}</Shortcut>
													</div>
												</div>
											</DropdownMenuItem>
										</DropdownMenuGroup>
										<DropdownMenuSeparator />
										<DropdownMenuGroup>
											<DropdownMenuItem
												Icon={FileText}
												onClick={() => handleExport("markdown")}
												disabled={isExporting}
											>
												Download as Markdown
											</DropdownMenuItem>
											<DropdownMenuItem
												Icon={Download}
												onClick={() => handleExport("pdf")}
												disabled={isExporting}
											>
												Download as PDF
											</DropdownMenuItem>
										</DropdownMenuGroup>
										<DropdownMenuSeparator />
										<DropdownMenuGroup>
											<DropdownMenuItem
												Icon={Trash}
												onClick={async () => {
													if (article.archived) {
														await mutateOk(
															zero.mutate(
																mutators.helpCenterArticles.update({
																	id: article.id,
																	archived: false,
																}),
															),
														)
														analytics.track("unarchive-helpcenterarticle", {
															id: article.id,
														})
													} else {
														setIsDeleteOpen(true)
														analytics.track(
															"open-archive-helpcenterarticle-dialog",
															{
																id: article.id,
															},
														)
													}
												}}
												className="flex w-full"
											>
												<div className="flex w-full items-center justify-between">
													<div>Delete</div>
													<div className="flex gap-1">
														<Shortcut>{ShortcutSymbols.CMD}</Shortcut>
														<Shortcut>{ShortcutSymbols.BACKSPACE}</Shortcut>
													</div>
												</div>
											</DropdownMenuItem>
										</DropdownMenuGroup>
									</DropdownMenu>
								</RoleCheck>
							</span>
						)}
					</div>
				</div>
			</div>
		</>
	)
}
